import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import type { AssistantAttachment, AssistantAttachmentKind } from '@/stores/assistantStore';
import { useNotify } from '@/stores/notificationStore';

/** Server allowlist (C3). 20 MiB, matching OS_BUDDY_ASSISTANT_FILE_MAX_BYTES. */
export const ASSISTANT_FILE_MAX_BYTES = 20 * 1024 * 1024;
export const ASSISTANT_FILE_MAX_COUNT = 5;

const ALLOWED_MIME_TYPES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/** Browsers often leave `File.type` empty for these. Images and PDF still need a MIME. */
const MIME_BY_EXTENSION: Record<string, string> = {
  csv: 'text/csv',
  json: 'application/json',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const ASSISTANT_FILE_ACCEPT = [
  ...ALLOWED_MIME_TYPES,
  '.csv',
  '.gif',
  '.jpeg',
  '.jpg',
  '.json',
  '.pdf',
  '.png',
  '.txt',
  '.webp',
  '.xlsx',
].join(',');

const SERVER_ERROR_CODES = [
  'UNSUPPORTED_TYPE',
  'TOO_LARGE',
  'FILE_INFECTED',
  'SCAN_UNAVAILABLE',
  'QUOTA_EXCEEDED',
  'PREPROCESS_FAILED',
] as const;

export type AssistantFileErrorCode = (typeof SERVER_ERROR_CODES)[number] | 'generic';

export type AssistantPendingFileStatus = 'uploading' | 'scanning' | 'ready' | 'error';

export interface AssistantPendingFile {
  localId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: AssistantPendingFileStatus;
  id?: string;
  kind?: AssistantAttachmentKind;
  errorCode?: AssistantFileErrorCode;
}

export interface AssistantFilesApi {
  files: AssistantPendingFile[];
  add: (incoming: readonly File[]) => void;
  remove: (localId: string) => void;
  clear: () => void;
  /** Put ready files back after a send that did not happen. Does not re-upload. */
  restore: (pending: readonly AssistantPendingFile[]) => void;
  readyIds: readonly string[];
  readyAttachments: readonly AssistantAttachment[];
  busy: boolean;
}

interface UploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
}

interface RegisteredAssistantFile {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: AssistantAttachmentKind;
  scanStatus: 'clean';
}

function extensionOf(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

function resolveMime(file: File): string | undefined {
  const raw = file.type.trim().toLowerCase();
  const mime = raw.split(';', 1)[0]?.trim() ?? '';
  if (mime.length > 0) {
    return ALLOWED_MIME_TYPES.has(mime) ? mime : undefined;
  }
  return MIME_BY_EXTENSION[extensionOf(file.name)];
}

function isServerErrorCode(value: unknown): value is (typeof SERVER_ERROR_CODES)[number] {
  return typeof value === 'string' && (SERVER_ERROR_CODES as readonly string[]).includes(value);
}

function errorCodeFrom(error: unknown): AssistantFileErrorCode {
  if (!(error instanceof ApiError)) return 'generic';
  const data = error.data;
  if (!data || typeof data !== 'object' || !('code' in data)) return 'generic';
  const code = (data as { code: unknown }).code;
  return isServerErrorCode(code) ? code : 'generic';
}

function isAttachmentKind(value: unknown): value is AssistantAttachmentKind {
  return value === 'image' || value === 'pdf' || value === 'text' || value === 'spreadsheet';
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Pending files for the Buddy composer.
 *
 * Each file is presigned, PUT to storage, then registered. Registration is the
 * virus scan, so that wait is `scanning`. Bytes never go to `/assistant/chat`.
 */
export function useAssistantFiles(): AssistantFilesApi {
  const { t } = useTranslation('assistant');
  const notify = useNotify();
  const [files, setFiles] = useState<AssistantPendingFile[]>([]);
  const filesRef = useRef<AssistantPendingFile[]>([]);
  const liveIds = useRef(new Set<string>());
  const uploads = useRef(new Map<string, AbortController>());

  const abortLocal = useCallback((localId: string) => {
    liveIds.current.delete(localId);
    const controller = uploads.current.get(localId);
    uploads.current.delete(localId);
    controller?.abort();
  }, []);

  useEffect(() => {
    const ids = liveIds.current;
    const controllers = uploads.current;
    return () => {
      ids.clear();
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
    };
  }, []);

  const commit = useCallback((next: AssistantPendingFile[]) => {
    filesRef.current = next;
    setFiles(next);
  }, []);

  const patch = useCallback(
    (localId: string, update: (file: AssistantPendingFile) => AssistantPendingFile) => {
      if (!liveIds.current.has(localId)) return;
      commit(filesRef.current.map((file) => (file.localId === localId ? update(file) : file)));
    },
    [commit],
  );

  const uploadOne = useCallback(
    async (localId: string, file: File, mimeType: string) => {
      const controller = uploads.current.get(localId);
      if (!controller) return;
      const { signal } = controller;
      const stillCurrent = () => liveIds.current.has(localId) && !signal.aborted;
      try {
        const presign = await api.post<UploadUrlResponse>(
          '/assistant/files/upload-url',
          {
            fileName: file.name,
            mimeType,
            sizeBytes: file.size,
          },
          { signal },
        );
        // Removed, cleared or unmounted files must not be stored or registered.
        if (!stillCurrent()) return;
        if (!presign.uploadUrl || !presign.fileKey) {
          throw new Error('upload url missing');
        }
        // The presign is signed for this Content-Type. An empty browser type
        // must still send the MIME we declared, or storage rejects the PUT.
        const uploadResponse = await fetch(presign.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': mimeType },
          signal,
        });
        if (!stillCurrent()) return;
        if (!uploadResponse.ok) {
          throw new Error('upload failed');
        }
        patch(localId, (current) => ({ ...current, status: 'scanning' }));
        const registered = await api.post<RegisteredAssistantFile>(
          '/assistant/files',
          {
            fileKey: presign.fileKey,
            originalFileName: file.name,
            mimeType,
          },
          { signal },
        );
        if (!stillCurrent()) return;
        if (!isAttachmentKind(registered.kind) || typeof registered.id !== 'string' || registered.id.length === 0) {
          patch(localId, (current) => ({ ...current, status: 'error', errorCode: 'generic' }));
          return;
        }
        patch(localId, (current) => ({
          ...current,
          status: 'ready',
          id: registered.id,
          fileName: typeof registered.fileName === 'string' && registered.fileName.length > 0
            ? registered.fileName
            : current.fileName,
          mimeType: typeof registered.mimeType === 'string' && registered.mimeType.length > 0
            ? registered.mimeType
            : current.mimeType,
          kind: registered.kind,
          sizeBytes: typeof registered.sizeBytes === 'number' ? registered.sizeBytes : current.sizeBytes,
        }));
      } catch (error) {
        if (!stillCurrent() || isAbortError(error)) return;
        patch(localId, (current) => ({
          ...current,
          status: 'error',
          errorCode: errorCodeFrom(error),
        }));
      } finally {
        if (uploads.current.get(localId) === controller) uploads.current.delete(localId);
      }
    },
    [patch],
  );

  const add = useCallback(
    (incoming: readonly File[]) => {
      if (incoming.length === 0) return;
      const room = ASSISTANT_FILE_MAX_COUNT - filesRef.current.length;
      if (incoming.length > room) {
        notify.warning(
          room > 0
            ? t('files.onlyFirstAdded', { count: room })
            : t('files.limitReached', { count: ASSISTANT_FILE_MAX_COUNT }),
        );
      }
      if (room <= 0) return;
      const created: AssistantPendingFile[] = [];
      const pendingUploads: Array<{ localId: string; file: File; mimeType: string }> = [];
      for (const file of incoming.slice(0, room)) {
        const localId = crypto.randomUUID();
        const mimeType = resolveMime(file);
        if (!mimeType) {
          created.push({
            localId,
            fileName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            status: 'error',
            errorCode: 'UNSUPPORTED_TYPE',
          });
          continue;
        }
        if (file.size > ASSISTANT_FILE_MAX_BYTES) {
          created.push({
            localId,
            fileName: file.name,
            mimeType,
            sizeBytes: file.size,
            status: 'error',
            errorCode: 'TOO_LARGE',
          });
          continue;
        }
        created.push({
          localId,
          fileName: file.name,
          mimeType,
          sizeBytes: file.size,
          status: 'uploading',
        });
        pendingUploads.push({ localId, file, mimeType });
      }
      for (const item of pendingUploads) {
        liveIds.current.add(item.localId);
        uploads.current.set(item.localId, new AbortController());
      }
      commit([...filesRef.current, ...created]);
      for (const item of pendingUploads) void uploadOne(item.localId, item.file, item.mimeType);
    },
    [commit, notify, t, uploadOne],
  );

  const remove = useCallback(
    (localId: string) => {
      abortLocal(localId);
      commit(filesRef.current.filter((file) => file.localId !== localId));
    },
    [abortLocal, commit],
  );

  const clear = useCallback(() => {
    for (const localId of [...liveIds.current]) abortLocal(localId);
    for (const controller of uploads.current.values()) controller.abort();
    uploads.current.clear();
    liveIds.current.clear();
    commit([]);
  }, [abortLocal, commit]);

  const restore = useCallback(
    (pending: readonly AssistantPendingFile[]) => {
      const have = new Set(filesRef.current.map((file) => file.localId));
      const room = ASSISTANT_FILE_MAX_COUNT - filesRef.current.length;
      if (room <= 0) return;
      const accepted = pending
        .filter(
          (file) =>
            file.status === 'ready' &&
            !have.has(file.localId) &&
            typeof file.id === 'string' &&
            file.id.length > 0 &&
            isAttachmentKind(file.kind),
        )
        .slice(0, room);
      if (accepted.length === 0) return;
      for (const file of accepted) liveIds.current.add(file.localId);
      commit([...filesRef.current, ...accepted]);
    },
    [commit],
  );

  const readyAttachments = useMemo(
    () =>
      files.flatMap((file): AssistantAttachment[] => {
        if (file.status !== 'ready' || !file.id || !file.kind) return [];
        return [{ id: file.id, fileName: file.fileName, mimeType: file.mimeType, kind: file.kind }];
      }),
    [files],
  );
  const readyIds = useMemo(() => readyAttachments.map((file) => file.id), [readyAttachments]);
  const busy = files.some((file) => file.status === 'uploading' || file.status === 'scanning');

  return { files, add, remove, clear, restore, readyIds, readyAttachments, busy };
}
